import { useCallback, useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { gapi } from "gapi-script";
import Swal from "sweetalert2";
import { API_KEY, DISCOVERY_DOCS } from "Component/Learning/Common/googleApi";
import { resetState, setField } from "Features/Learning/TestimonyProgram/addAssetsSlice";
import { submitAssets } from "Features/Learning/TestimonyProgram/Utils/TestimonyProgramUtils";
import DropzoneCommon from "../../../Common/Upload/Dropzone";
import imageCompression from "browser-image-compression";

const IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1280,
  useWebWorker: true,
  initialQuality: 0.8, // Sedikit menaikkan kualitas awal untuk hasil yang lebih baik
  alwaysKeepResolution: true, // Tidak mengubah resolusi jika sudah di bawah maxWidthOrHeight
};

const Index = ({ id }) => {
  const dispatch = useDispatch();
  const history = useNavigate();

  const [modal, setModal] = useState(false);
  const [isGapiInitialized, setIsGapiInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  const { isLoading: isSubmitting, url: assets } = useSelector(state => state.addAssets);

  const toggle = () => !isProcessing && setModal(!modal);

  /**
   * Logika kompresi yang terpusat.
   * Fungsi ini akan mengecek apakah kompresi diperlukan sebelum menjalankannya.
   */
  const getCompressedImage = useCallback(async (file) => {
    const fileSizeInMB = file.size / 1024 / 1024;

    // Cek jika ukuran file sudah di bawah batas. Jika ya, langsung kembalikan file asli.
    if (fileSizeInMB <= IMAGE_COMPRESSION_OPTIONS.maxSizeMB) {
      console.log(`✅ File "${file.name}" sudah optimal (${fileSizeInMB.toFixed(2)} MB). Lewati kompresi.`);
      return file;
    }

    console.log(`⏳ Mengompres file "${file.name}"... Ukuran awal: ${fileSizeInMB.toFixed(2)} MB`);
    try {
      const compressedFile = await imageCompression(file, IMAGE_COMPRESSION_OPTIONS);
      const compressedSizeInMB = compressedFile.size / 1024 / 1024;
      console.log(`✅ Kompresi berhasil. Ukuran baru: ${compressedSizeInMB.toFixed(2)} MB`);
      return compressedFile;
    } catch (error) {
      console.error(`❌ Gagal mengompres file "${file.name}":`, error);
      Swal.fire('Error', `Gagal mengompres gambar ${file.name}.`, 'error');
      // Kembalikan null atau lemparkan error untuk menandakan kegagalan
      return null;
    }
  }, []);

  /**
   * Menangani perubahan status file dari Dropzone.
   */
  const handleChangeStatus = useCallback(async ({ file }, status) => {
    if (status === "removed") {
      dispatch(setField({ field: "url", value: assets?.filter((f) => f.name !== file.name) }));
      return;
    }

    if (status === "done") {
      setIsProcessing(true);
      setProcessingMessage("Mengompres gambar...");

      const processedFile = await getCompressedImage(file);
      if (processedFile) {
        dispatch(setField({ field: "url", value: [...assets, processedFile] }));
      }

      setIsProcessing(false);
      setProcessingMessage("");
    }
  }, [assets, dispatch, getCompressedImage]);

  /**
  * Menangani submit form.
  */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!assets || assets.length === 0) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'File tidak boleh kosong.' });
      return;
    }
    dispatch(submitAssets({ assets, successCallback: handleSuccess, id }));
  };

  /**
   * Callback setelah submit berhasil.
   */
  const handleSuccess = () => {
    dispatch(resetState());
    setModal(false);
    history(`${process.env.PUBLIC_URL}/testimony-program/detail/${id}`);
  };

  // --- Google Drive Integration ---
  useEffect(() => {
    if (modal && !isGapiInitialized) {
      gapi.load('client', async () => {
        try {
          await gapi.client.init({ apiKey: API_KEY, discoveryDocs: DISCOVERY_DOCS });
          console.log('✅ GAPI client initialized');
          setIsGapiInitialized(true);
        } catch (error) {
          console.error('❌ Error initializing GAPI client:', error);
          Swal.fire('Error', 'Gagal menginisialisasi Google API.', 'error');
        }
      });
    }
  }, [modal, isGapiInitialized]);

  // 🔘 Login & Picker
  const handleLoginAndPickFile = () => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/drive.readonly',
      callback: (response) => {
        if (response.access_token) {
          launchDrivePicker(response.access_token);
        } else {
          alert('❌ Gagal login');
        }
      },
    });

    client.requestAccessToken();
  };

  // 📁 Picker
  const launchDrivePicker = (accessToken) => {
    setModal(false);
    gapi.load('picker', () => {
      const view = new window.google.picker.View(window.google.picker.ViewId.DOCS_IMAGES);
      const picker = new window.google.picker.PickerBuilder()
        .setAppId(process.env.REACT_APP_GOOGLE_DRIVE_PROJECT_NUMBER)
        .setOAuthToken(accessToken)
        .setDeveloperKey(process.env.REACT_APP_GOOGLE_DRIVE_API_KEY)
        .addView(view)
        .addView(new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS).setSelectFolderEnabled(true))
        .setCallback((data) => pickerCallback(data, accessToken))
        .build();
      picker.setVisible(true);
    });
  };

  /**
   * Callback setelah file dipilih dari Google Drive.
   * Dibuat lebih bersih dengan memisahkan logika pemrosesan file.
   */
  const processAndUploadDriveFiles = async (files, accessToken) => {
    setIsProcessing(true);
    setProcessingMessage('Mengambil & mengompres file dari Drive...');

    try {
      const filePromises = files.map(async (fileInfo) => {
        const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileInfo.id}?alt=media`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const blob = await res.blob();
        const file = new File([blob], fileInfo.name, { type: fileInfo.mimeType });
        return getCompressedImage(file);
      });

      const compressedFiles = (await Promise.all(filePromises)).filter(Boolean); // Filter null jika ada kegagalan kompresi

      if (compressedFiles.length > 0) {
        dispatch(submitAssets({
          assets: [...assets, ...compressedFiles],
          successCallback: handleSuccess,
          id
        }));
      }
    } catch (err) {
      console.error('❌ Error memproses file dari Drive:', err);
      Swal.fire('Gagal', 'Terjadi kesalahan saat mengambil atau mengompres file dari Drive.', 'error');
    } finally {
      setIsProcessing(false);
      setProcessingMessage("");
    }
  };

  // 📥 Callback setelah pilih file
  const pickerCallback = async (data, accessToken) => {
    if (data.action !== window.google.picker.Action.PICKED) return;

    setModal(false);

    const pickedDoc = data.docs[0];
    if (pickedDoc.mimeType === 'application/vnd.google-apps.folder') {
      // Logika untuk mengambil file dari folder
      const query = encodeURIComponent(`'${pickedDoc.id}' in parents and trashed = false and mimeType contains 'image/'`);
      const res = await fetch(`https://www.googleapis.com/drive/v3/files?q=${query}&fields=files(id,name,mimeType)`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const { files } = await res.json();
      if (files && files.length > 0) {
        processAndUploadDriveFiles(files, accessToken);
      }
    } else {
      // Logika untuk file tunggal
      processAndUploadDriveFiles([pickedDoc], accessToken);
    }
  };

  const isLoading = isProcessing || isSubmitting;

  return (
    <>
      <Button color="primary" disabled={isLoading} onClick={toggle} style={{ padding: '0px 12px' }}>
        <i className="fa fa-solid fa-image me-2"></i>
        {isLoading ? "Memproses..." : "Tambah Gambar"}
      </Button>

      <Modal isOpen={isLoading} backdrop="static" size="sm" centered>
        <ModalBody className="text-center">
          <i className="spinner-border spinner-border-sm me-2" />
          {processingMessage || 'Mengunggah file ke server...'}
        </ModalBody>
      </Modal>

      <Modal isOpen={modal} toggle={toggle} backdrop="static" size="lg">
        <ModalHeader toggle={toggle}>Tambahkan Gambar ke Group</ModalHeader>
        <ModalBody>
          <DropzoneCommon
            label={'File Gambar Testimoni'}
            inputContent={assets?.length > 0 ? 'Tambah file lagi...' : 'Seret & lepas file gambar di sini'}
            handleChangeStatus={handleChangeStatus}
            accept={'image/*'}
            gapiInitialized={isGapiInitialized}
            handleLoginAndPickFile={handleLoginAndPickFile}
            disabled={isLoading}
            maxFiles={"unlimited"}
            height={"100%"}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit} disabled={isLoading || assets.length === 0}>
            {isSubmitting ? 'Menyimpan...' : 'Simpan'}
          </Button>{' '}
          <Button color="secondary" onClick={toggle} disabled={isLoading}>Batal</Button>
        </ModalFooter>
      </Modal>
    </>
  );

}

export default Index